import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { flushAnalytics, listingProps, track } from '../analytics/events.js';
import {
    blue, blueBright, green, greenBright, pink, pinkBright, purple, purpleBright, red, redBright, scoreToColor, yellow,
} from './areaData.js';
import {
    buildSqmNote, getAreaLabel, getSqmComparison, SCALE_LABELS,
} from './listingStory.js';
import BalconyOutlinedIcon from '@mui/icons-material/BalconyOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeckOutlinedIcon from '@mui/icons-material/DeckOutlined';
import ElevatorOutlinedIcon from '@mui/icons-material/ElevatorOutlined';
import FireplaceOutlinedIcon from '@mui/icons-material/FireplaceOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import FavoriteButton from './FavoriteButton.js';
import ListingPhotoStrip from './ListingPhotoStrip.js';
import ListingCommute from './ListingCommute.js';
import { TripHero, TripTile } from './CommuteTrip.js';
import { useCommuteRoute, useTravelAvailable } from './commute.js';
import { tripModes } from './tripMap.js';
import { detectFloorplans } from './floorplanDetect.js';
import { listingShareUrl } from './listingDetailsApi.js';
import { publicOrigin } from './publicOrigin.js';
import { LEGACY_FALLBACK_LOGO, LISTING_IMAGE_PLACEHOLDER } from './listingPlaceholder.js';
import AreaPriceChart from './AreaPriceChart.js';
import './listingFullView.css';
import './priceHistory.css';

const FALLBACK_IMAGE = LISTING_IMAGE_PLACEHOLDER;
// The collage shows up to 5 photos; the rest are in the full-screen gallery.
const MAX_COLLAGE_PHOTOS = 5;
const DEFAULT_INTEREST_RATE = 3.25;

// The map's 11-step score scale, worst to best.
const SCALE_COLORS = [red, redBright, yellow, greenBright, green, blueBright, blue, pinkBright, pink, purpleBright, purple];

const LFV_ICON_PATHS = {
    close: 'M6 6l12 12M18 6 6 18',
    chevron: 'M9 6l6 6-6 6',
    back: 'M15 18l-6-6 6-6',
    external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
    grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
    plan: 'M3 3h18v18H3zM3 11h8V3M11 15v6M15 11h6',
    trend: 'M4 7l6 6 4-4 6 6M15 15h5v-5',
    tag: 'M3 12V4h8l10 10-8 8zM7.5 8h.01',
    work: 'M4 8h16v11H4zM9 8V5h6v3M4 13h16',
    key: 'M8 19a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM11 12l8-8M16 7l2 2M14 9l2 2',
    school: 'M2 9l10-5 10 5-10 5zM6 11v5c3 2.5 9 2.5 12 0v-5',
    wallet: 'M4 7h15a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4zM4 7l11-3v3M16 13h.01',
    // Share: three nodes joined (the regular share glyph; phone only).
    share: 'M18 2a3 3 0 1 0 0 6 3 3 0 1 0 0-6M6 9a3 3 0 1 0 0 6 3 3 0 1 0 0-6M18 16a3 3 0 1 0 0 6 3 3 0 1 0 0-6M8.6 10.6l6.8-3.7M8.6 13.4l6.8 3.7',
};
// Copy link uses MUI's ContentCopyRounded (the two-overlapping-squares copy glyph) and CheckRounded once copied.

// Copy text to the clipboard, falling back to a hidden textarea + execCommand where the async API is blocked.
const copyTextToClipboard = (text) => {
    try {
        if (navigator.clipboard?.writeText) {
            return navigator.clipboard.writeText(text).catch(() => copyTextFallback(text));
        }
    } catch (error) {
        /* fall through to the textarea path */
    }
    return Promise.resolve(copyTextFallback(text));
};

const copyTextFallback = (text) => {
    try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
    } catch (error) {
        /* nothing more we can do */
    }
};

const LfvIcon = ({ name }) => (
    <svg className="lfv-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={LFV_ICON_PATHS[name] || ''} />
    </svg>
);

const normalizeListingType = (type, swedish) => {
    const normalized = String(type || '').toLowerCase().trim();
    if (normalized === 'apartment' || normalized === 'lagenhet' || normalized === 'lägenhet') {
        return swedish ? 'Lägenhet' : 'Apartment';
    }
    if (
        normalized === 'rowhouse'
        || normalized === 'townhouse'
        || normalized === 'radhus'
        || normalized === 'parhus'
        || normalized === 'par/radhus'
        || normalized === 'house++'
    ) {
        return swedish ? 'Par/Radhus' : 'Row house';
    }
    if (normalized === 'fritidshus') {
        return swedish ? 'Fritidshus' : 'Holiday home';
    }
    return swedish ? 'Villa/Hus' : 'House';
};

const getListingKind = (type) => {
    const normalized = String(type || '').toLowerCase().trim();
    if (['apartment', 'lagenhet', 'lägenhet'].includes(normalized)) return 'apartment';
    // 'house++' is the API's code for par/radhus (the details response carries it unconverted).
    if (['rowhouse', 'townhouse', 'radhus', 'parhus', 'par/radhus', 'house++'].includes(normalized)) return 'rowhouse';
    return 'house';
};

const normalizeScore = (value) => {
    if (value == null) return null;
    const parsed = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return null;
    const clamped = Math.min(Math.max(parsed, 1), 6);
    return Math.round(clamped * 2) / 2;
};

const parseAreaValue = (listing) => {
    if (!listing) return null;
    if (Number.isFinite(listing.areaValue) && listing.areaValue > 0) {
        return listing.areaValue;
    }
    const fromText = String(listing.area || '').replace(',', '.').match(/(\d+(\.\d+)?)/);
    if (!fromText) return null;
    const parsed = Number(fromText[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const formatAreaText = (listing, swedish) => {
    const raw = String(listing?.area || '').trim();
    if (raw) {
        if (/m²|kvm|m2/i.test(raw)) {
            return raw.replace(/kvm|m2/gi, 'm²');
        }
        return `${raw} m²`;
    }
    const areaValue = parseAreaValue(listing);
    if (!Number.isFinite(areaValue)) return swedish ? 'Saknas' : 'Missing';
    return `${Math.round(areaValue)} m²`;
};

// Dark text on the light score colours, white on the dark ones.
const readableTextOn = (hex) => {
    const value = String(hex || '').replace('#', '');
    if (value.length !== 6) return '#ffffff';
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 0.6 ? '#13241f' : '#ffffff';
};

const formatScoreText = (value, swedish) => {
    if (value == null) return '–';
    const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
    return swedish ? text.replace('.', ',') : text;
};

const formatPriceText = (value, fallback) => {
    if (value == null) return fallback;
    const raw = String(value).trim();
    if (!raw) return fallback;

    const lower = raw.toLowerCase();
    const numericLike = /^[\d\s.,]+$/.test(raw);
    const hasCurrency = /\b(kr|sek)\b/.test(lower);
    if (!numericLike && !hasCurrency) {
        return raw;
    }

    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;
    // A price of 0 means there is none yet (upcoming sales).
    if (Number(digits) === 0) return fallback;
    const parsed = Number(digits);
    if (!Number.isFinite(parsed)) return raw;

    return `${parsed.toLocaleString('sv-SE').replace(/\u00a0/g, ' ')}kr`;
};

const formatSwedishInteger = (value) => (
    Number(value).toLocaleString('sv-SE', { maximumFractionDigits: 0 }).replace(/\u00a0/g, ' ')
);

const formatSwedishNumber = (value, maximumFractionDigits = 1) => (
    Number(value).toLocaleString('sv-SE', { maximumFractionDigits }).replace(/\u00a0/g, ' ')
);

// 3 980 000 -> "3,98" (sv) / "3.98" (en), in millions.
const formatMillionsNumber = (value, swedish) => {
    const millions = Number(value) / 1e6;
    const digits = millions >= 10 ? 1 : 2;
    return millions.toLocaleString(swedish ? 'sv-SE' : 'en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
// One amount or a range: "3,98–4,79 mkr" / "SEK 3.98–4.79m".
const formatMillions = (swedish, low, high = null) => {
    const amount = [low, high].filter((value) => value != null).map((value) => formatMillionsNumber(value, swedish)).join('–');
    return swedish ? `${amount} mkr` : `SEK ${amount}m`;
};

// Amenity keys from the listing's `extra.amenities`, in the order they're shown.
const AMENITY_LABELS = {
    elevator: ['Hiss', 'Elevator', ElevatorOutlinedIcon],
    balcony: ['Balkong', 'Balcony', BalconyOutlinedIcon],
    patio: ['Uteplats', 'Patio', DeckOutlinedIcon],
    fireplace: ['Eldstad', 'Fireplace', FireplaceOutlinedIcon],
};

const parseNumericLoose = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value == null) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    // Pick the first standalone numeric chunk to avoid concatenating multiple values
    // from strings such as "45 000 kr/m² avgift 2 300 kr/mån".
    const tokenMatch = raw.match(/-?\d{1,3}(?:[\s\u00a0.,]\d{3})+(?:[.,]\d+)?|-?\d+(?:[.,]\d+)?/);
    const token = tokenMatch?.[0] ? String(tokenMatch[0]).trim() : '';
    if (!token) return null;

    const compactToken = token.replace(/\u00a0/g, ' ').replace(/\s+/g, '');
    const normalizedToken = compactToken.includes(',') && !compactToken.includes('.')
        ? compactToken.replace(',', '.')
        : compactToken;
    const numericOnly = normalizedToken.replace(/[^0-9.-]/g, '');
    if (numericOnly) {
        const parsed = Number(numericOnly);
        if (Number.isFinite(parsed)) return parsed;
    }

    const digitsOnly = token.replace(/\D/g, '');
    if (!digitsOnly) return null;
    const parsedDigits = Number(digitsOnly);
    return Number.isFinite(parsedDigits) ? parsedDigits : null;
};

const getSwedishAmortizationRate = (loanToValueRatio) => {
    if (!Number.isFinite(loanToValueRatio) || loanToValueRatio <= 0) return 0;
    // Base Swedish amortization requirement by belaningsgrad (LTV).
    // The separate +1% debt-to-income rule is not applied here because income input is not collected.
    if (loanToValueRatio > 0.7) return 0.02;
    if (loanToValueRatio > 0.5) return 0.01;
    return 0;
};

const formatMonthlyCost = (value, fallback = '–') => {
    if (!Number.isFinite(value)) return fallback;
    return `${formatSwedishInteger(Math.max(0, Math.round(value)))} kr/mån`;
};

const clampInterestRate = (value) => Math.min(25, Math.max(0, value));

const sanitizeInterestInput = (value) => String(value || '')
    .replace(/[^0-9,.\s]/g, '')
    .replace(/\s+/g, '');

const formatInterestRateInput = (value) => formatSwedishNumber(clampInterestRate(value), 2);

const asText = (value, fallback) => {
    if (value == null) return fallback;
    const normalized = String(value).trim();
    return normalized || fallback;
};

const hasDisplayValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'number') return Number.isFinite(value);
    return String(value).trim().length > 0;
};

const getImageUrl = (entry) => {
    if (entry == null) return '';
    if (typeof entry === 'string') return entry.trim();
    if (typeof entry === 'object') {
        const candidate = (
            entry.url
            ?? entry.src
            ?? entry.image
            ?? entry.href
            ?? entry.link
            ?? ''
        );
        return String(candidate || '').trim();
    }
    return String(entry).trim();
};

const getImageIdentifier = (entry) => {
    if (!entry || typeof entry !== 'object') return '';
    const candidate = (
        entry.identifier
        ?? entry.id
        ?? entry.kind
        ?? entry.type
        ?? entry.tag
        ?? entry.label
        ?? ''
    );
    return String(candidate || '').trim().toLowerCase();
};

const looksLikePlanritning = (entry, url = '') => {
    const identifier = getImageIdentifier(entry);
    const target = `${identifier} ${String(url || '').toLowerCase()}`;
    return /planritning|plan-ritning|floorplan|floor-plan|ritning|tba/.test(target);
};

const isEditableTarget = (target) => {
    if (!target || typeof target !== 'object') return false;
    const element = target;
    const tagName = typeof element.tagName === 'string' ? element.tagName.toUpperCase() : '';
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return true;
    if (Boolean(element.isContentEditable)) return true;
    if (typeof element.closest === 'function') {
        return Boolean(element.closest('[contenteditable="true"]'));
    }
    return false;
};

const ListingFullViewModal = ({
    open,
    listing,
    onClose,
    languageState,
    areaScore = null,
    areaMetrics = null,
    detailsLoading = false,
    detailsError = '',
    darkMode = false,
    wide = false,
    // Screen point ({ x, y }) of the map marker it was opened from: the popup grows out of it.
    openFrom = null,
    // Open with the gallery at the floor plans (listvy's card floor plan tile).
    openAtFloorplan = false,
}) => {
    const swedish = Boolean(languageState?.swedish);
    const fallbackValue = swedish ? 'Saknas' : 'Missing';
    const [imageIndex, setImageIndex] = useState(0);
    const [interestRateValue, setInterestRateValue] = useState(DEFAULT_INTEREST_RATE);
    const [interestRateInput, setInterestRateInput] = useState(formatInterestRateInput(DEFAULT_INTEREST_RATE));
    const [downPaymentInput, setDownPaymentInput] = useState('');
    const [showMortgageBreakdown, setShowMortgageBreakdown] = useState(false);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const activeThumbRef = useRef(null);

    // Floor plans the source didn't label, found among the last photos (floorplanDetect.js).
    const [detectedPlans, setDetectedPlans] = useState({ listing: null, urls: [] });
    const listingHasPlan = useMemo(() => (
        (Array.isArray(listing?.planritning) && listing.planritning.length > 0)
        || (Array.isArray(listing?.images) && listing.images.some((entry) => looksLikePlanritning(entry, getImageUrl(entry))))
    ), [listing]);
    useEffect(() => {
        if (!open || listingHasPlan || !Array.isArray(listing?.images) || listing.images.length < 3) return undefined;
        let cancelled = false;
        detectFloorplans(listing.images.map(getImageUrl)).then((urls) => {
            if (!cancelled && urls.length) setDetectedPlans({ listing, urls });
        });
        return () => { cancelled = true; };
    }, [listing, listingHasPlan, open]);

    const galleryItems = useMemo(() => {
        const detected = new Set(detectedPlans.listing === listing ? detectedPlans.urls : []);
        const base = [];
        const seen = new Map();
        const pushImage = (entry, forcePlanritning = false) => {
            const value = getImageUrl(entry);
            if (!value || value === LEGACY_FALLBACK_LOGO) return;
            const isPlanritning = forcePlanritning || detected.has(value) || looksLikePlanritning(entry, value);
            const existing = seen.get(value);
            if (existing != null) {
                if (isPlanritning && base[existing] && !base[existing].isPlanritning) {
                    base[existing] = { ...base[existing], isPlanritning: true };
                }
                return;
            }
            seen.set(value, base.length);
            base.push({ url: value, isPlanritning });
        };

        if (Array.isArray(listing?.images)) {
            listing.images.forEach((entry) => {
                pushImage(entry, false);
            });
        }
        if (Array.isArray(listing?.planritning)) {
            listing.planritning.forEach((entry) => {
                pushImage(entry, true);
            });
        }
        if (!base.length && listing?.image) {
            pushImage(listing.image, false);
        }
        if (!base.length) {
            base.push({ url: FALLBACK_IMAGE, isPlanritning: false });
        }
        const regularItems = base.filter((entry) => !entry.isPlanritning);
        const planritningItems = base.filter((entry) => entry.isPlanritning);
        return [...regularItems, ...planritningItems];
    }, [detectedPlans, listing]);
    const images = useMemo(() => galleryItems.map((entry) => entry.url), [galleryItems]);
    const firstPlanritningIndex = useMemo(
        () => galleryItems.findIndex((entry) => entry.isPlanritning),
        [galleryItems],
    );
    const hasPlanritning = firstPlanritningIndex >= 0;
    const thumbItems = useMemo(() => galleryItems
        .map((entry, index) => ({ ...entry, index }))
        .filter((entry) => !entry.isPlanritning)
        .slice(0, MAX_COLLAGE_PHOTOS), [galleryItems]);
    const listingIdentity = useMemo(
        () => String(listing?.homeId || listing?.id || listing?.address || listing?.title || ''),
        [listing?.homeId, listing?.id, listing?.address, listing?.title],
    );

    // Every photo (not the floor plans) for the swipeable main photo, and which one it shows.
    const photoItems = useMemo(() => galleryItems
        .map((entry, index) => ({ ...entry, index }))
        .filter((entry) => !entry.isPlanritning), [galleryItems]);
    const [heroPosition, setHeroPosition] = useState(0);

    // The trip to work (Restid), drawn in the main photo's place when its collage tile or the Till jobbet
    // row's "Visa resan" is clicked (desktop; phones show it in the Till jobbet card). `tripMode` is the means
    // of travel the map shows, the chosen one until another is picked.
    const travelAvailable = useTravelAvailable();
    const commuteRoute = useCommuteRoute(listing);
    const [tripShown, setTripShown] = useState(false);
    const [tripModePick, setTripModePick] = useState(null);
    const [phoneLayout, setPhoneLayout] = useState(() => (
        typeof window !== 'undefined' && Boolean(window.matchMedia?.('(max-width: 700px)').matches)
    ));
    useEffect(() => {
        const query = window.matchMedia?.('(max-width: 700px)');
        if (!query) return undefined;
        const update = () => setPhoneLayout(query.matches);
        update();
        query.addEventListener?.('change', update);
        return () => query.removeEventListener?.('change', update);
    }, []);
    const tripModeList = useMemo(
        () => (travelAvailable === true && commuteRoute.place && commuteRoute.status === 'done'
            ? tripModes(commuteRoute.route, commuteRoute.mode)
            : []),
        [travelAvailable, commuteRoute.place, commuteRoute.status, commuteRoute.route, commuteRoute.mode],
    );
    const tripMode = [tripModePick, commuteRoute.mode].find((mode) => tripModeList.includes(mode)) || tripModeList[0] || null;
    const tripHome = Number.isFinite(Number(listing?.lat)) && Number.isFinite(Number(listing?.lng))
        ? [Number(listing.lat), Number(listing.lng)]
        : null;
    const tripWork = commuteRoute.place ? [commuteRoute.place.lat, commuteRoute.place.lng] : null;
    const canDrawTrip = Boolean(tripMode);
    const heroShowsTrip = tripShown && canDrawTrip && !phoneLayout;
    const showTrip = useCallback((from) => {
        setTripShown(true);
        track('commute.trip_map', { action: 'open', from, mode: tripMode || '' });
    }, [tripMode]);
    const pickTripMode = useCallback((mode) => {
        setTripModePick(mode);
        track('commute.trip_map', { action: 'mode', mode });
    }, []);

    useEffect(() => {
        setImageIndex(0);
        setGalleryOpen(false);
        setHeroPosition(0);
        setTripShown(false);
        setTripModePick(null);
    }, [listing?.id, listing?.address, listing?.title]);
    // Opened at the floor plans: they're last in the gallery, so their index moves when the full details (all
    // photos) arrive; follow it until the details are in. (After the reset above, which runs when the listing changes.)
    const floorplanOpenPendingRef = useRef(true);
    useEffect(() => {
        if (!open) {
            floorplanOpenPendingRef.current = true;
            return;
        }
        if (!openAtFloorplan || !floorplanOpenPendingRef.current || firstPlanritningIndex < 0) return;
        setImageIndex(firstPlanritningIndex);
        setGalleryOpen(true);
        if (!detailsLoading) floorplanOpenPendingRef.current = false;
    }, [open, openAtFloorplan, firstPlanritningIndex, detailsLoading]);

    useEffect(() => {
        if (!galleryOpen) return;
        activeThumbRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [galleryOpen, imageIndex]);

    // The gallery is one vertical feed of every photo; the photo nearest the middle of the feed is the
    // current one (highlighted in the thumbnail grid). Clicking a thumbnail or an arrow key jumps there.
    const galleryFeedRef = useRef(null);
    const galleryItemRefs = useRef([]);
    const imageIndexRef = useRef(0);
    imageIndexRef.current = imageIndex;
    // While a jump glides there, the photos it passes don't take the highlight (it would flicker back and
    // forth between them); the feed's position counts again once the scroll has stopped.
    const galleryGlidingRef = useRef(null); // { target, at } while a jump glides
    const jumpToImage = useCallback((index, smooth = false) => {
        const node = galleryItemRefs.current[index];
        if (node && smooth) galleryGlidingRef.current = { target: index, at: performance.now() };
        if (node) node.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
        setImageIndex(index);
    }, []);
    useEffect(() => {
        if (!galleryOpen) return undefined;
        // Open on the photo that was clicked.
        galleryGlidingRef.current = null;
        const frame = window.requestAnimationFrame(() => jumpToImage(imageIndexRef.current));
        return () => window.cancelAnimationFrame(frame);
    }, [galleryOpen, jumpToImage]);
    const nearestGalleryIndex = useCallback(() => {
        const feed = galleryFeedRef.current;
        if (!feed) return imageIndexRef.current;
        const box = feed.getBoundingClientRect();
        const middle = box.top + box.height / 2;
        let best = imageIndexRef.current;
        let bestDistance = Infinity;
        galleryItemRefs.current.forEach((node, index) => {
            if (!node) return;
            const r = node.getBoundingClientRect();
            const distance = Math.abs((r.top + r.bottom) / 2 - middle);
            if (distance < bestDistance) { bestDistance = distance; best = index; }
        });
        return best;
    }, []);
    const onGalleryScroll = useCallback(() => {
        const best = nearestGalleryIndex();
        if (best !== imageIndexRef.current) setImageIndex(best);
    }, [nearestGalleryIndex]);
    const galleryScrollFrameRef = useRef(0);
    const galleryGlideTimerRef = useRef(0);
    const handleGalleryScrollEnd = useCallback(() => {
        window.clearTimeout(galleryGlideTimerRef.current);
        const glide = galleryGlidingRef.current;
        if (!glide) return;
        // A scroll that was cut short (e.g. by the next jump starting) doesn't end the glide early.
        if (nearestGalleryIndex() !== glide.target && performance.now() - glide.at < 1500) {
            galleryGlideTimerRef.current = window.setTimeout(handleGalleryScrollEnd, 300);
            return;
        }
        galleryGlidingRef.current = null;
        onGalleryScroll();
    }, [nearestGalleryIndex, onGalleryScroll]);
    const handleGalleryScroll = useCallback(() => {
        if (galleryGlidingRef.current) {
            // Browsers without scrollend: the glide is over once the scroll events stop.
            window.clearTimeout(galleryGlideTimerRef.current);
            galleryGlideTimerRef.current = window.setTimeout(handleGalleryScrollEnd, 300);
            return;
        }
        if (galleryScrollFrameRef.current) return;
        galleryScrollFrameRef.current = window.requestAnimationFrame(() => {
            galleryScrollFrameRef.current = 0;
            onGalleryScroll();
        });
    }, [onGalleryScroll, handleGalleryScrollEnd]);

    // Desktop: one small scroll (a wheel notch or a short swipe on a trackpad) glides to the next or
    // previous photo. The rest of that gesture (trackpad momentum, a fast-spun wheel) is swallowed: a new
    // jump needs a short pause or, after a moment, a fresh push (stronger than the fading momentum, or a
    // wheel that keeps turning).
    useEffect(() => {
        const feed = galleryFeedRef.current;
        if (!galleryOpen || !feed || !window.matchMedia?.('(min-width: 701px)').matches) return undefined;
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let lastEventAt = 0;
        let jumpedAt = 0;
        let lastDelta = 0;
        let pending = 0;
        // Each gesture adds up its movement until it is enough for a jump (a trackpad starts with 1–3 px
        // events), jumps once, and swallows the rest.
        let gestureJumped = false;
        const onWheel = (event) => {
            if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
            event.preventDefault();
            const now = performance.now();
            const delta = Math.abs(event.deltaY) * (event.deltaMode === 1 ? 40 : 1);
            const freshGesture = now - lastEventAt > 180
                // momentum only fades; a held-on wheel (steady notches) moves on every 0.6 s
                || (now - jumpedAt > 600 && (delta > lastDelta * 1.5 + 4 || (delta >= 50 && delta >= lastDelta)));
            lastEventAt = now;
            lastDelta = delta;
            if (freshGesture) {
                pending = 0;
                gestureJumped = false;
            }
            if (gestureJumped) return;
            pending += Math.sign(event.deltaY) * delta;
            if (Math.abs(pending) < 6) return;
            const count = galleryItemRefs.current.filter(Boolean).length;
            const next = Math.min(count - 1, Math.max(0, imageIndexRef.current + Math.sign(pending)));
            pending = 0;
            jumpedAt = now;
            gestureJumped = true;
            if (next !== imageIndexRef.current) jumpToImage(next, smooth);
        };
        feed.addEventListener('wheel', onWheel, { passive: false });
        return () => feed.removeEventListener('wheel', onWheel);
    }, [galleryOpen, jumpToImage]);
    // scrollend (React 18 has no prop for it) ends a glide as soon as the feed stops.
    useEffect(() => {
        const feed = galleryFeedRef.current;
        if (!galleryOpen || !feed) return undefined;
        feed.addEventListener('scrollend', handleGalleryScrollEnd);
        return () => {
            feed.removeEventListener('scrollend', handleGalleryScrollEnd);
            window.clearTimeout(galleryGlideTimerRef.current);
        };
    }, [galleryOpen, handleGalleryScrollEnd]);

    // Analytics: per opened listing, how many photos/floor plans were looked at, how long the view stayed
    // open, and whether the housing-cost calculator was used (once per field).
    const engagementRef = useRef(null);
    useEffect(() => {
        if (!open || !listingIdentity) return undefined;
        engagementRef.current = { openedAt: Date.now(), viewed: new Set([0]), floorplan: false, calc: new Set() };
        return () => {
            const engagement = engagementRef.current;
            engagementRef.current = null;
            if (!engagement) return;
            track('listing.photos', { count: engagement.viewed.size, floorplan: engagement.floorplan });
            track('listing.close', { seconds: Math.round((Date.now() - engagement.openedAt) / 1000) });
        };
    }, [open, listingIdentity]);
    useEffect(() => {
        const engagement = engagementRef.current;
        if (!engagement) return;
        engagement.viewed.add(imageIndex);
        if (galleryItems[imageIndex]?.isPlanritning) engagement.floorplan = true;
    }, [galleryItems, imageIndex]);
    const trackCalc = (field) => {
        const engagement = engagementRef.current;
        if (!engagement || engagement.calc.has(field)) return;
        engagement.calc.add(field);
        track('calc.change', { field });
    };
    const trackRealtorClick = () => {
        track('listing.realtor_click', { ...listingProps(listing), score: areaScore ?? undefined });
        flushAnalytics();  // the realtor page opens in a new tab: send the conversion right away
    };

    useEffect(() => {
        setInterestRateValue(DEFAULT_INTEREST_RATE);
        setInterestRateInput(formatInterestRateInput(DEFAULT_INTEREST_RATE));
        setShowMortgageBreakdown(false);
        const basePrice = Number.isFinite(listing?.priceValue)
            ? Number(listing.priceValue)
            : parseNumericLoose(listing?.price);
        if (Number.isFinite(basePrice) && basePrice > 0) {
            setDownPaymentInput(formatSwedishInteger(Math.round(basePrice * 0.10)));
            return;
        }
        setDownPaymentInput('');
    }, [listingIdentity, listing?.price, listing?.priceValue]);

    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    // Back (the phone's swipe or the browser button) closes the listing instead of leaving the page: opening it
    // adds a history entry — carrying the listing's own URL (?bostad=/annons=), so the address bar shows a
    // shareable link while the popup is open — and back pops it. Closing it any other way (×, Escape, the
    // backdrop) takes that entry off again, so a later back leaves the page as usual and the URL goes back to
    // the plain view.
    const shareUrl = useMemo(() => (open ? listingShareUrl(listing) : null), [open, listing]);
    const shareUrlRef = useRef(shareUrl);
    shareUrlRef.current = shareUrl;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (!open || typeof window === 'undefined' || !window.history?.pushState) return undefined;
        let entryOnTop = true;
        window.history.pushState({ ...(window.history.state || {}), listingFullView: true }, '', shareUrlRef.current || null);
        const onPopState = () => {
            entryOnTop = false;
            onCloseRef.current?.();
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            if (entryOnTop && window.history.state?.listingFullView) window.history.back();
        };
    }, [open]);

    // Opening another listing without closing first (a second marker on the map) keeps the same history entry
    // but updates its URL in place, so the address bar always matches the listing on screen.
    useEffect(() => {
        if (!open || typeof window === 'undefined' || !window.history?.replaceState) return;
        if (!window.history.state?.listingFullView || !shareUrl) return;
        window.history.replaceState(window.history.state, '', shareUrl);
    }, [open, shareUrl]);

    // Sharing the listing: a copy-link button (everywhere) and, on phones with a native share sheet, a share
    // button beside it. The copy button confirms with a short "Länk kopierad!" before resetting.
    const [linkCopied, setLinkCopied] = useState(false);
    const copyResetRef = useRef(null);
    useEffect(() => () => { if (copyResetRef.current) clearTimeout(copyResetRef.current); }, []);
    const canNativeShare = useMemo(() => {
        if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
        try {
            return window.matchMedia('(pointer: coarse)').matches;
        } catch (error) {
            return false;
        }
    }, []);
    // The absolute URL to share: the current location with the listing's share param set (?bostad=/annons=).
    const absoluteShareHref = useCallback(() => {
        const relative = listingShareUrl(listing);
        try {
            return relative ? new URL(relative, publicOrigin()).href : window.location.href;
        } catch (error) {
            return typeof window !== 'undefined' ? window.location.href : '';
        }
    }, [listing]);
    const handleCopyLink = useCallback((event) => {
        event?.stopPropagation?.();
        copyTextToClipboard(absoluteShareHref());
        setLinkCopied(true);
        if (copyResetRef.current) clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setLinkCopied(false), 1600);
        track('listing.share', { method: 'copy', ...listingProps(listing) });
    }, [absoluteShareHref, listing]);
    const handleNativeShare = useCallback((event) => {
        event?.stopPropagation?.();
        const shareTitle = String(listing?.address || listing?.title || '').trim();
        try {
            const sharePromise = navigator.share({
                title: shareTitle ? (swedish ? `Bostad: ${shareTitle}` : `Home: ${shareTitle}`) : 'hempejl',
                url: absoluteShareHref(),
            });
            if (sharePromise?.catch) sharePromise.catch(() => {});
        } catch (error) {
            /* the user dismissed the sheet, or it isn't available */
        }
        track('listing.share', { method: 'native', ...listingProps(listing) });
    }, [absoluteShareHref, listing, swedish]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (galleryOpen) setGalleryOpen(false);
                else onClose?.();
                return;
            }
            // Arrow keys page through photos in the full-screen gallery.
            if (!galleryOpen || images.length <= 1) return;
            const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
            if (isEditableTarget(event.target) || isEditableTarget(activeElement)) return;
            const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
            if (step) {
                event.preventDefault();
                const next = Math.min(images.length - 1, Math.max(0, imageIndexRef.current + step));
                jumpToImage(next, true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [galleryOpen, images.length, jumpToImage, onClose, open]);

    useEffect(() => {
        if (!open || !images.length) return undefined;
        const total = images.length;
        const normalizeIndex = (value) => ((value % total) + total) % total;
        const targetIndexes = new Set([
            normalizeIndex(imageIndex),
            normalizeIndex(imageIndex + 1),
            normalizeIndex(imageIndex + 2),
            normalizeIndex(imageIndex - 1),
        ]);
        targetIndexes.forEach((index) => {
            const url = String(images[index] || '').trim();
            if (!url) return;
            const img = new Image();
            img.src = url;
        });
        return undefined;
    }, [imageIndex, images, open]);

    const activeImage = images[Math.min(imageIndex, images.length - 1)] || FALLBACK_IMAGE;


    // Opened from a map marker: a circle grows from the marker until it reveals the whole popup, which
    // eases up from 96 %, while the backdrop fades in. Once per opening; none with reduced motion.
    const overlayRef = useRef(null);
    const shellRef = useRef(null);
    const openAnimatedRef = useRef(false);
    useLayoutEffect(() => {
        if (!open) {
            openAnimatedRef.current = false;
            return;
        }
        if (openAnimatedRef.current) return;
        openAnimatedRef.current = true;
        const shell = shellRef.current;
        const overlay = overlayRef.current;
        if (!shell || !overlay || typeof shell.animate !== 'function') return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
        const easing = 'cubic-bezier(0.2, 0.75, 0.2, 1)';
        // The backdrop's colour and blur, not the overlay's opacity (that would fade the popup too).
        const backdrop = window.getComputedStyle(overlay);
        overlay.animate([
            { backgroundColor: 'rgba(0, 0, 0, 0)', backdropFilter: 'blur(0px)' },
            { backgroundColor: backdrop.backgroundColor, backdropFilter: backdrop.backdropFilter || 'none' },
        ], { duration: 260, easing: 'ease-out' });
        if (!openFrom || !Number.isFinite(openFrom.x) || !Number.isFinite(openFrom.y)) return;
        const rect = shell.getBoundingClientRect();
        const ox = openFrom.x - rect.left;
        const oy = openFrom.y - rect.top;
        // Far enough to cover the farthest corner.
        const radius = Math.max(
            Math.hypot(ox, oy), Math.hypot(rect.width - ox, oy),
            Math.hypot(ox, rect.height - oy), Math.hypot(rect.width - ox, rect.height - oy),
        );
        shell.animate([
            { clipPath: `circle(12px at ${ox}px ${oy}px)`, transform: 'scale(0.96)', transformOrigin: `${ox}px ${oy}px` },
            { clipPath: `circle(${Math.ceil(radius)}px at ${ox}px ${oy}px)`, transform: 'scale(1)', transformOrigin: `${ox}px ${oy}px` },
        ], { duration: 420, easing });
    }, [open, openFrom]);

    if (!open || !listing || typeof document === 'undefined') {
        return null;
    }

    const title = asText(
        listing.address || listing.title,
        swedish ? 'Okänd adress' : 'Unknown address',
    );
    const subtitle = asText(
        [listing.ort || listing.postort, listing.kommun].filter(Boolean).join(' · ') || listing.subtitle,
        '',
    );
    const score = normalizeScore(areaScore ?? listing.betyg ?? listing.rating ?? null);
    const showPlot = hasDisplayValue(listing.plot);
    const typeValue = normalizeListingType(listing.type, swedish);
    const areaText = formatAreaText(listing, swedish);
    const roomsValue = asText(listing.rooms, '');
    const roomsDisplay = hasDisplayValue(roomsValue)
        ? `${swedish ? String(roomsValue).replace('.', ',') : roomsValue} ${swedish ? 'rum' : 'rooms'}`
        : '';
    const areaNumeric = parseAreaValue(listing);
    const explicitSqmNumeric = parseNumericLoose(listing.sqmPrice);
    const priceNumeric = Number.isFinite(listing?.priceValue)
        ? Number(listing.priceValue)
        : parseNumericLoose(listing?.price);
    const computedSqmNumeric = Number.isFinite(priceNumeric) && Number.isFinite(areaNumeric) && areaNumeric > 0
        ? Math.round(priceNumeric / areaNumeric)
        : null;
    const finalSqmNumeric = Number.isFinite(computedSqmNumeric) && computedSqmNumeric > 0
        ? computedSqmNumeric
        : (Number.isFinite(explicitSqmNumeric) && explicitSqmNumeric > 0 ? Math.round(explicitSqmNumeric) : null);
    const sqmPriceValue = Number.isFinite(finalSqmNumeric) ? formatSwedishInteger(finalSqmNumeric) : '';
    const feeValue = asText(listing.fee, '');
    const plotValue = asText(listing.plot, '');
    const cityValue = asText(listing.ort || listing.postort, '');
    const municipalityValue = asText(listing.kommun, '');
    const daysValue = asText(listing.days, '');
    const brokerValue = asText(listing.broker, '');
    const realtorUrl = String(listing.realtorUrl || '').trim();
    const hasRealtorLink = Boolean(realtorUrl);
    const isMissingValue = (value) => !hasDisplayValue(value) || value === fallbackValue;
    const publishedText = !isMissingValue(daysValue) ? daysValue : '';
    const subtitleLocation = [cityValue, municipalityValue].filter(Boolean).join(' · ');
    const listPrice = Number.isFinite(priceNumeric) && priceNumeric > 0 ? Math.round(priceNumeric) : null;
    const minimumDownPayment = Number.isFinite(listPrice) ? Math.round(listPrice * 0.10) : null;
    const maximumDownPayment = Number.isFinite(listPrice) ? Math.round(listPrice) : null;
    const downPaymentNumeric = parseNumericLoose(downPaymentInput);
    const downPaymentValue = Number.isFinite(minimumDownPayment) && Number.isFinite(maximumDownPayment)
        ? Math.min(
            Math.max(
                Number.isFinite(downPaymentNumeric) ? downPaymentNumeric : minimumDownPayment,
                minimumDownPayment,
            ),
            maximumDownPayment,
        )
        : null;
    const loanAmount = listPrice != null && Number.isFinite(downPaymentValue)
        ? Math.max(listPrice - downPaymentValue, 0)
        : null;
    const ltv = listPrice != null && listPrice > 0 && Number.isFinite(loanAmount)
        ? loanAmount / listPrice
        : null;
    const amortizationRate = getSwedishAmortizationRate(ltv);
    const interestRatePercent = Number.isFinite(interestRateValue)
        ? Math.max(interestRateValue, 0)
        : 0;
    const monthlyInterest = Number.isFinite(loanAmount)
        ? (loanAmount * (interestRatePercent / 100)) / 12
        : null;
    const monthlyAmortization = Number.isFinite(loanAmount)
        ? (loanAmount * amortizationRate) / 12
        : null;
    const feeNumeric = parseNumericLoose(feeValue);
    const monthlyFee = Number.isFinite(feeNumeric)
        ? Math.max(feeNumeric, 0)
        : 0;
    const monthlyBeforeDeduction = Number.isFinite(monthlyInterest) && Number.isFinite(monthlyAmortization)
        ? monthlyInterest + monthlyAmortization + monthlyFee
        : null;
    const yearlyInterest = Number.isFinite(monthlyInterest) ? monthlyInterest * 12 : null;
    const yearlyTaxDeduction = Number.isFinite(yearlyInterest)
        ? Math.min(yearlyInterest, 100000) * 0.3
        : null;
    const monthlyTaxDeduction = Number.isFinite(yearlyTaxDeduction)
        ? yearlyTaxDeduction / 12
        : null;
    const monthlyAfterDeduction = Number.isFinite(monthlyBeforeDeduction) && Number.isFinite(monthlyTaxDeduction)
        ? monthlyBeforeDeduction - monthlyTaxDeduction
        : null;
    const ltvText = Number.isFinite(ltv)
        ? `${formatSwedishNumber(ltv * 100, 1)}%`
        : fallbackValue;
    const amortizationRateText = `${formatSwedishNumber(amortizationRate * 100, 1)}%`;
    const onInterestRateChange = (event) => {
        trackCalc('rate');
        const sanitized = sanitizeInterestInput(event.target.value);
        setInterestRateInput(sanitized);
        const parsed = parseNumericLoose(sanitized);
        if (Number.isFinite(parsed)) {
            setInterestRateValue(clampInterestRate(parsed));
        }
    };
    const onInterestRateBlur = () => {
        const parsed = parseNumericLoose(interestRateInput);
        if (Number.isFinite(parsed)) {
            const clamped = clampInterestRate(parsed);
            setInterestRateValue(clamped);
            setInterestRateInput(formatInterestRateInput(clamped));
            return;
        }
        setInterestRateInput(formatInterestRateInput(interestRateValue));
    };
    const onDownPaymentChange = (event) => {
        trackCalc('cash');
        const normalized = String(event.target.value || '').replace(/[^0-9,.\s]/g, '');
        setDownPaymentInput(normalized);
    };
    const onDownPaymentBlur = () => {
        if (!Number.isFinite(minimumDownPayment) || !Number.isFinite(maximumDownPayment)) return;
        const parsed = parseNumericLoose(downPaymentInput);
        const clamped = Math.min(
            Math.max(Number.isFinite(parsed) ? parsed : minimumDownPayment, minimumDownPayment),
            maximumDownPayment,
        );
        setDownPaymentInput(formatSwedishInteger(Math.round(clamped)));
    };
    const kind = getListingKind(listing.type);
    const isApartment = kind === 'apartment';
    const area = areaMetrics;
    const areaScores = area?.scores || {};
    const place = area?.name || cityValue;
    const scoreColor = score != null ? (scoreToColor[score] || '#8f9aab') : '#8f9aab';
    const areaLabel = getAreaLabel(score, swedish);
    const scaleLabels = SCALE_LABELS[swedish ? 'sv' : 'en'];
    // Centre of the score's step on the 11-step colour scale.
    const scorePosition = score != null ? ((((score - 1) * 2) + 0.5) / 11) * 100 : null;
    const sqmComparison = getSqmComparison({ listingSqm: finalSqmNumeric, isApartment, area });
    const sqmNote = buildSqmNote(sqmComparison, { swedish });
    const feeNumericValue = parseNumericLoose(feeValue);
    const feeDisplay = Number.isFinite(feeNumericValue) ? `${formatSwedishInteger(feeNumericValue)} kr` : feeValue;
    const plotNumeric = parseNumericLoose(plotValue);
    const plotDisplay = Number.isFinite(plotNumeric) && /^[\d\s.,]+$/.test(String(plotValue))
        ? `${formatSwedishInteger(plotNumeric)} m²`
        : plotValue;
    const priceText = formatPriceText(listing.price, swedish ? 'Pris saknas' : 'No price').replace(/(\d)kr$/, '$1 kr');
    const monthlyText = Number.isFinite(monthlyBeforeDeduction)
        ? `${formatSwedishInteger(Math.round(monthlyBeforeDeduction))} kr/${swedish ? 'mån' : 'month'}`
        : '';
    const monthlyAfterText = Number.isFinite(monthlyAfterDeduction)
        ? `${formatSwedishInteger(Math.round(monthlyAfterDeduction))} kr`
        : '';

    // Fields beyond the basics (listingExtra, from the details fetch).
    const extra = listing.extra && typeof listing.extra === 'object' ? listing.extra : {};
    const positive = (value) => {
        const number = parseNumericLoose(value);
        return Number.isFinite(number) && number > 0 ? number : null;
    };
    const constructionYear = positive(extra.constructionYear);
    const floorNumber = parseNumericLoose(extra.floor);
    const additionalArea = positive(extra.additionalArea);
    const factRooms = hasDisplayValue(roomsDisplay) && positive(roomsValue)
        ? { id: 'fact-rooms', value: roomsDisplay, label: typeValue }
        : { id: 'fact-type', value: typeValue, label: swedish ? 'Typ' : 'Type' };
    const factArea = { id: 'fact-area', value: areaText, label: swedish ? 'Boarea' : 'Living area' };
    const factFee = {
        id: 'fact-fee',
        value: feeDisplay,
        // A villa's monthly cost is its operating cost; the others pay a fee.
        label: kind === 'house' ? (swedish ? 'Drift/mån' : 'Running costs/month') : (swedish ? 'Avgift/mån' : 'Fee/month'),
    };
    const factPlot = showPlot ? { id: 'fact-plot', value: plotDisplay, label: swedish ? 'Tomt' : 'Plot' } : null;
    const factYear = constructionYear ? { id: 'fact-year', value: String(constructionYear), label: swedish ? 'Byggår' : 'Built' } : null;
    const factFloor = Number.isFinite(floorNumber)
        ? { id: 'fact-floor', value: formatSwedishNumber(floorNumber), label: swedish ? 'Våning' : 'Floor' }
        : null;
    const factAdditional = additionalArea
        ? { id: 'fact-additional', value: `${formatSwedishNumber(additionalArea)} m²`, label: swedish ? 'Biarea' : 'Secondary area' }
        : null;
    // Area price development (price_history.py, served inside the full listing as priceHistory). Shown as its
    // own mini chart (AreaPriceChart) under the area stats, with a 1/2/3/Max-year picker (default 3 years).
    const priceHistory = listing.priceHistory || null;
    const priceTrendArea = priceHistory && priceHistory.area;
    const hasAreaTrend = Boolean(priceTrendArea) && Array.isArray(priceTrendArea.series) && priceTrendArea.series.length >= 4;
    // The home's own kr/m² for the chart's marker: the backend's anchor, else the listing's computed value.
    const homeSqmForChart = (priceHistory && Number.isFinite(priceHistory.anchorSqmPrice) && priceHistory.anchorSqmPrice > 0)
        ? priceHistory.anchorSqmPrice
        : (Number.isFinite(finalSqmNumeric) && finalSqmNumeric > 0 ? finalSqmNumeric : null);
    const factSqm = sqmPriceValue ? {
        id: 'fact-sqm',
        value: `${sqmPriceValue} kr`,
        label: sqmNote?.tone === 'good'
            ? `${swedish ? 'Per m²' : 'Per m²'}, ${sqmNote.title}`
            : (swedish ? 'Per m²' : 'Per m²'),
        good: sqmNote?.tone === 'good',
    } : null;
    const hasFactValue = (fact) => fact && !isMissingValue(fact.value)
        // A 0 (rooms, fee) means the source has no value.
        && !/^0(?:[\s,.]|$)/.test(String(fact.value));
    // Four large numbers, then the rest on one line (details), per home type.
    const keyFactsByKind = {
        apartment: [factRooms, factArea, factFee, factSqm],
        house: [factRooms, factArea, factPlot, factSqm],
        rowhouse: [factRooms, factArea, factFee, factSqm],
    };
    // All of the kind's fact slots (fee loads with the details); render them all so the grid keeps its
    // column count while loading, skeletoning the ones not in yet.
    const keyFactSlots = (keyFactsByKind[kind] || keyFactsByKind.house).filter(Boolean);
    const keyFacts = keyFactSlots.filter(hasFactValue);
    const tenureText = asText(extra.tenureForm, '');
    const coopText = kind !== 'house' ? asText(extra.housingCoopName, '') : '';
    // The details line: each fact as a short phrase ("Våning 1", "Byggår 1950", "Bostadsrätt").
    const phrase = (fact, prefix, suffix = '') => (hasFactValue(fact) ? `${prefix}${fact.value}${suffix}` : '');
    const floorPhrase = phrase(factFloor, swedish ? 'Våning ' : 'Floor ');
    const yearPhrase = phrase(factYear, swedish ? 'Byggår ' : 'Built ');
    const detailsByKind = {
        apartment: [floorPhrase, yearPhrase, tenureText, coopText],
        house: [phrase(factAdditional, swedish ? 'Biarea ' : 'Secondary area '), yearPhrase, phrase(factFee, swedish ? 'Drift ' : 'Running costs ', swedish ? '/mån' : '/month'), tenureText],
        rowhouse: [yearPhrase, phrase(factPlot, swedish ? 'Tomt ' : 'Plot '), tenureText, coopText],
    };
    const amenityKeys = new Set(Array.isArray(extra.amenities) ? extra.amenities : []);
    const chips = [
        ...Object.entries(AMENITY_LABELS)
            .filter(([key]) => amenityKeys.has(key))
            .map(([key, [sv, en, Icon]]) => ({ id: key, label: swedish ? sv : en, Icon })),
        ...(extra.isNewConstruction
            ? [{ id: 'new', label: swedish ? 'Nyproduktion' : 'New build', Icon: NewReleasesOutlinedIcon }]
            : []),
    ];
    const details = [...(detailsByKind[kind] || detailsByKind.house), ...chips.map((chip) => chip.label)].filter(Boolean);

    // Price card notes: the valuation range, and the first asking price when it has been lowered.
    const estimateLow = positive(extra.estimateLow);
    const estimateHigh = positive(extra.estimateHigh);
    const estimatePrice = positive(extra.estimatePrice);
    let valuationText = '';
    if (estimateLow && estimateHigh && estimateHigh > estimateLow) {
        valuationText = `${swedish ? 'Värdering' : 'Valuation'} ${formatMillions(swedish, estimateLow, estimateHigh)}`;
    } else if (estimatePrice) {
        valuationText = `${swedish ? 'Värdering ca' : 'Valuation about'} ${formatMillions(swedish, estimatePrice)}`;
    }
    const firstPrice = positive(extra.firstPrice);
    const loweredText = firstPrice && Number.isFinite(priceNumeric) && firstPrice > priceNumeric
        ? `${swedish ? 'Sänkt från' : 'Lowered from'} ${formatSwedishInteger(firstPrice)} kr`
        : '';
    // Asking less than the valuation's low end (or its single figure) is worth saying.
    const valuationFloor = estimateLow && estimateHigh && estimateHigh > estimateLow ? estimateLow : estimatePrice;
    const underValuation = Boolean(valuationText && valuationFloor && Number.isFinite(priceNumeric) && priceNumeric > 0 && priceNumeric < valuationFloor);
    const priceNotes = [valuationText, loweredText].filter(Boolean);
    const renderPriceNotes = (placement) => (priceNotes.length ? (
        <div className={`lfv-price-notes lfv-price-notes--${placement}`}>
            {priceNotes.map((note) => (
                <span key={note}>
                    {note}
                    {note === valuationText && underValuation
                        ? <b className="lfv-under-valuation"> · {swedish ? 'under värderingen' : 'below the valuation'}</b>
                        : null}
                </span>
            ))}
        </div>
    ) : null);

    const statItems = [
        {
            id: 'income',
            label: swedish ? 'Nettoinkomst/mån' : 'Net income/month',
            value: Number.isFinite(area?.incomeMonthlyTkr) ? `${formatSwedishNumber(area.incomeMonthlyTkr)} ${swedish ? 'tkr' : 'kSEK'}` : '',
            score: areaScores.income,
        },
        {
            id: 'unemployment',
            label: swedish ? 'Arbetslöshet' : 'Unemployment',
            value: Number.isFinite(area?.unemploymentPercent) ? `${formatSwedishNumber(area.unemploymentPercent)} %` : '',
            score: areaScores.unemployment,
        },
        {
            id: 'owned',
            label: swedish ? 'Ägda bostäder' : 'Owned homes',
            value: Number.isFinite(area?.ownedPercent) ? `${formatSwedishNumber(area.ownedPercent, 0)} %` : '',
            score: areaScores.owned,
        },
        {
            id: 'education',
            label: swedish ? 'Högutbildade' : 'Highly educated',
            value: Number.isFinite(area?.educationPercent) ? `${formatSwedishNumber(area.educationPercent, 0)} %` : '',
            score: areaScores.education,
        },
        {
            id: 'apartment-sqm',
            label: swedish ? 'Lägenhet per m²' : 'Apartment per m²',
            value: Number.isFinite(area?.apartmentSqm) ? `${formatSwedishInteger(area.apartmentSqm)} kr` : '',
            score: areaScores.apartmentPrice,
        },
        {
            id: 'house-sqm',
            label: swedish ? 'Hus per m²' : 'House per m²',
            value: Number.isFinite(area?.houseSqm) ? `${formatSwedishInteger(area.houseSqm)} kr` : '',
            score: areaScores.housePrice,
        },
    ].filter((item) => item.value);

    const collageSource = thumbItems.length ? thumbItems : [{ url: activeImage, index: 0 }];
    // While the full details (all photos) are still loading, show the full 5-tile collage up front with
    // skeleton tiles, so the photo area doesn't restructure from one photo into a grid when they arrive.
    const photosPending = detailsLoading && collageSource.length < 5;
    // The trip to work takes the first small tile (its photo is still in the main photo's strip), so a
    // listing with a single photo gets a second tile for it.
    const collageSize = Math.max(
        photosPending ? 5 : (collageSource.length >= 5 ? 5 : (collageSource.length >= 3 ? 3 : collageSource.length)),
        canDrawTrip ? 2 : 0,
    );
    const collageItems = collageSource.slice(0, collageSize);
    const photoCount = galleryItems.filter((entry) => !entry.isPlanritning).length;

    const openGallery = (index, from) => {
        setImageIndex(index);
        setGalleryOpen(true);
        track('listing.gallery', { from, photos: photoCount });
    };
    // "Så har vi räknat": the monthly cost broken down, under either calculator.
    const renderCostBreakdown = (placement) => (
        <>
            <button
                type="button"
                className={`lfv-disclosure${showMortgageBreakdown ? ' is-open' : ''}`}
                onClick={() => setShowMortgageBreakdown((prev) => !prev)}
                aria-expanded={showMortgageBreakdown}
                aria-controls={`listing-mortgage-breakdown-${placement}`}
            >
                {swedish ? 'Så har vi räknat' : 'How we calculated'}
                <LfvIcon name="chevron" />
            </button>
            {showMortgageBreakdown ? (
                <dl id={`listing-mortgage-breakdown-${placement}`} className="lfv-breakdown">
                    <div><dt>{swedish ? 'Lån' : 'Loan'}</dt><dd>{Number.isFinite(loanAmount) ? `${formatSwedishInteger(loanAmount)} kr` : fallbackValue}</dd></div>
                    <div><dt>{swedish ? 'Belåningsgrad' : 'Loan-to-value'}</dt><dd>{ltvText}</dd></div>
                    <div><dt>{swedish ? 'Ränta' : 'Interest'}</dt><dd>{formatMonthlyCost(monthlyInterest, fallbackValue)}</dd></div>
                    <div><dt>{swedish ? 'Amortering' : 'Amortization'} ({amortizationRateText})</dt><dd>{formatMonthlyCost(monthlyAmortization, fallbackValue)}</dd></div>
                    <div><dt>{swedish ? 'Avgift' : 'Fee'}</dt><dd>{formatMonthlyCost(monthlyFee, fallbackValue)}</dd></div>
                    <div className="is-total"><dt>{swedish ? 'Totalt' : 'Total'}</dt><dd>{formatMonthlyCost(monthlyBeforeDeduction, fallbackValue)}</dd></div>
                    <div><dt>{swedish ? 'Efter ränteavdrag (30 %)' : 'After interest deduction (30%)'}</dt><dd>{formatMonthlyCost(monthlyAfterDeduction, fallbackValue)}</dd></div>
                </dl>
            ) : null}
        </>
    );

    // The monthly cost, then the calculator's two fields and "Så har vi räknat": in the price card on wider screens,
    // and the same on phones in a card of its own in the story column (the price card is a bottom bar there); CSS
    // shows one of them, so each placement has its own ids.
    const renderMonthlyCost = () => (monthlyText ? (
        <div className="lfv-month">
            <span>{swedish ? 'Boendekostnad' : 'Monthly cost'}</span>
            <span className="lfv-month-value">
                {swedish ? 'ca ' : 'about '}{monthlyText}
                {monthlyAfterText ? <small>{monthlyAfterText} {swedish ? 'efter ränteavdrag' : 'after deduction'}</small> : null}
            </span>
        </div>
    ) : null);

    const renderCardCostFields = (placement) => (
        <div className="lfv-card-cost">
            <div className="lfv-card-fields">
                <label className="lfv-field" htmlFor={`listing-mortgage-down-payment-${placement}`}>
                    <span>{swedish ? 'Kontantinsats' : 'Down payment'}</span>
                    <span className="lfv-field-box">
                        <input
                            id={`listing-mortgage-down-payment-${placement}`}
                            type="text"
                            inputMode="numeric"
                            value={downPaymentInput}
                            onChange={onDownPaymentChange}
                            onBlur={onDownPaymentBlur}
                        />
                        <i>kr</i>
                    </span>
                </label>
                <label className="lfv-field" htmlFor={`listing-mortgage-interest-rate-${placement}`}>
                    <span>{swedish ? 'Ränta' : 'Interest'}</span>
                    <span className="lfv-field-box">
                        <input
                            id={`listing-mortgage-interest-rate-${placement}`}
                            type="text"
                            inputMode="decimal"
                            value={interestRateInput}
                            onChange={onInterestRateChange}
                            onBlur={onInterestRateBlur}
                        />
                        <i>%</i>
                    </span>
                </label>
            </div>
            {renderCostBreakdown(placement)}
        </div>
    );

    const renderCostSection = (placement) => (
        <section className={`lfv-cost lfv-cost--${placement}`} aria-label={swedish ? 'Boendekostnad' : 'Monthly cost'}>
            <h3 className="lfv-card-title">{swedish ? 'Boendekostnad' : 'Monthly cost'}</h3>
            {monthlyText ? (
                <div className="lfv-month-value">
                    {swedish ? 'ca ' : 'about '}{monthlyText}
                    {monthlyAfterText ? <small>{monthlyAfterText} {swedish ? 'efter ränteavdrag' : 'after deduction'}</small> : null}
                </div>
            ) : null}
            {renderCardCostFields(placement)}
        </section>
    );

    const realtorButton = (className) => (hasRealtorLink ? (
        <a
            className={`lfv-realtor ${className}`}
            href={realtorUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackRealtorClick}
        >
            {swedish ? 'Besök mäklaren' : 'Visit the realtor'}
            <LfvIcon name="external" />
        </a>
    ) : (
        <button type="button" className={`lfv-realtor ${className}`} disabled aria-disabled="true">
            {swedish ? 'Besök mäklaren' : 'Visit the realtor'}
        </button>
    ));

    return createPortal(
        <div
            ref={overlayRef}
            className={`listing-fullview-overlay${darkMode ? ' is-dark' : ''}`}
            onClick={onClose}
            role="presentation"
        >
            <section
                ref={shellRef}
                className={`listing-fullview-shell${wide ? ' is-wide' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={swedish ? `Bostad: ${title}` : `Home: ${title}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="lfv-actions">
                    <FavoriteButton listing={listing} swedish={swedish} source="popup" className="lfv-favorite" />
                    <button
                        className="lfv-close"
                        type="button"
                        onClick={onClose}
                        aria-label={swedish ? 'Stäng' : 'Close'}
                    >
                        <LfvIcon name="close" />
                    </button>
                </div>
                <div className="lfv-scroll">
                    <div className={`lfv-collage lfv-collage--${collageSize}`}>
                        {/* The main photo can be swiped through every photo; the small ones show theirs in it. */}
                        <div className="lfv-tile lfv-tile--hero">
                            <ListingPhotoStrip
                                items={photoItems.length ? photoItems : collageItems}
                                position={heroPosition}
                                onPositionChange={setHeroPosition}
                                onOpen={(index) => openGallery(index, 'collage')}
                                title={title}
                                swedish={swedish}
                            />
                            {heroShowsTrip && (
                                <TripHero
                                    route={commuteRoute.route}
                                    modes={tripModeList}
                                    mode={tripMode}
                                    onModeChange={pickTripMode}
                                    home={tripHome}
                                    work={tripWork}
                                    workLabel={commuteRoute.place?.label}
                                    swedish={swedish}
                                    dark={darkMode}
                                    photoCount={photoCount}
                                    onShowPhotos={() => setTripShown(false)}
                                />
                            )}
                        </div>
                        {Array.from({ length: Math.max(0, collageSize - 1) }).map((_, slot) => {
                            if (slot === 0 && canDrawTrip) {
                                return (
                                    <TripTile
                                        key="collage-trip"
                                        route={commuteRoute.route}
                                        mode={tripMode}
                                        home={tripHome}
                                        work={tripWork}
                                        swedish={swedish}
                                        dark={darkMode}
                                        shown={heroShowsTrip}
                                        onOpen={() => (heroShowsTrip ? setTripShown(false) : showTrip('tile'))}
                                    />
                                );
                            }
                            const item = collageItems[slot + 1];
                            if (!item) {
                                // A photo not loaded yet: a skeleton tile keeps the grid in place.
                                return <div key={`collage-skeleton-${slot}`} className="lfv-tile lfv-tile--skeleton" aria-hidden="true" />;
                            }
                            const slide = photoItems.findIndex((entry) => entry.index === item.index);
                            return (
                                <button
                                    key={`collage-${item.url}-${item.index}`}
                                    type="button"
                                    className={`lfv-tile${slide === heroPosition && !heroShowsTrip ? ' is-shown' : ''}`}
                                    onClick={() => {
                                        setTripShown(false);
                                        if (slide >= 0) setHeroPosition(slide);
                                        else openGallery(item.index, 'collage');
                                    }}
                                    aria-label={swedish ? `Visa bild ${item.index + 1}` : `Show image ${item.index + 1}`}
                                >
                                    <img src={item.url} alt="" loading="eager" />
                                </button>
                            );
                        })}
                        <div className="lfv-collage-badges" hidden={heroShowsTrip}>
                            {listing.imgTag ? <span className="lfv-photo-tag">{listing.imgTag}</span> : null}
                        </div>
                        <div className="lfv-collage-actions">
                            {hasPlanritning && (
                                <button
                                    type="button"
                                    className="lfv-photo-action"
                                    onClick={() => openGallery(firstPlanritningIndex, 'floorplan')}
                                >
                                    <LfvIcon name="plan" />
                                    <span>{swedish ? 'Planritning' : 'Floor plan'}</span>
                                </button>
                            )}
                            {images.length > 1 && (
                                <button type="button" className="lfv-photo-action" onClick={() => openGallery(0, 'all')}>
                                    <LfvIcon name="grid" />
                                    <span className="lfv-photo-action-long">
                                        {swedish ? `Visa alla ${photoCount} bilder` : `Show all ${photoCount} photos`}
                                    </span>
                                    <span className="lfv-photo-action-short">{Math.min(heroPosition + 1, photoCount)} / {photoCount}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="lfv-body">
                        <div className="lfv-main">
                            <header className="lfv-head">
                                <div className="lfv-head-text">
                                    <h2>{title}</h2>
                                    <p className="lfv-location">
                                        {subtitleLocation || subtitle}
                                        {publishedText ? <span className="lfv-new">{publishedText}</span> : null}
                                    </p>
                                </div>
                                <div className="lfv-head-actions">
                                    {canNativeShare && (
                                        <button
                                            className="lfv-iconbtn lfv-share"
                                            type="button"
                                            onClick={handleNativeShare}
                                            aria-label={swedish ? 'Dela' : 'Share'}
                                            title={swedish ? 'Dela' : 'Share'}
                                        >
                                            <LfvIcon name="share" />
                                        </button>
                                    )}
                                    <button
                                        className={`lfv-iconbtn lfv-copy${linkCopied ? ' is-copied' : ''}`}
                                        type="button"
                                        onClick={handleCopyLink}
                                        aria-label={swedish ? 'Kopiera länk' : 'Copy link'}
                                        title={swedish ? 'Kopiera länk' : 'Copy link'}
                                    >
                                        {linkCopied
                                            ? <CheckRoundedIcon className="lfv-mui-icon" />
                                            : <ContentCopyRoundedIcon className="lfv-mui-icon" />}
                                        <span className="lfv-copy-tip" role="status" aria-live="polite">
                                            {linkCopied ? (swedish ? 'Länk kopierad!' : 'Link copied!') : ''}
                                        </span>
                                    </button>
                                </div>
                            </header>

                            {listing.favoriteRemoved ? (
                                <div className="lfv-status" aria-live="polite">
                                    {swedish
                                        ? 'Den här bostaden finns inte längre till salu på sajten.'
                                        : 'This home is no longer for sale on the site.'}
                                </div>
                            ) : null}

                            {detailsError ? (
                                <div className="lfv-status is-error" aria-live="polite">{detailsError}</div>
                            ) : null}

                            {(keyFactSlots.length > 0 || details.length > 0 || detailsLoading) && (
                                <section className="lfv-home" aria-label={swedish ? 'Bostaden' : 'The home'}>
                                    <h3 className="lfv-card-title">{swedish ? 'Bostaden' : 'The home'}</h3>
                                    {keyFactSlots.length > 0 && (
                                        <div className="lfv-keyfacts">
                                            {keyFactSlots.map((fact) => (
                                                <div key={fact.id} className={fact.good ? 'is-good' : undefined}>
                                                    {hasFactValue(fact)
                                                        ? <b>{fact.value}</b>
                                                        : <b className={detailsLoading ? 'lfv-skel lfv-skel--num' : undefined}>{detailsLoading ? ' ' : '–'}</b>}
                                                    <span>{fact.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* The details line always reserves its row, so it doesn't appear and push the
                                        layout down once the extra facts load. */}
                                    <ul className="lfv-details">
                                        {details.length > 0
                                            ? details.map((detail) => <li key={detail}>{detail}</li>)
                                            : (detailsLoading ? <li className="lfv-skel lfv-skel--line" aria-hidden="true">&nbsp;</li> : null)}
                                    </ul>
                                </section>
                            )}

                            <ListingCommute
                                listing={listing}
                                swedish={swedish}
                                trip={canDrawTrip ? {
                                    modes: tripModeList,
                                    mode: tripMode,
                                    onModeChange: pickTripMode,
                                    home: tripHome,
                                    work: tripWork,
                                    shown: heroShowsTrip,
                                    phone: phoneLayout,
                                    dark: darkMode,
                                    onShow: () => showTrip('row'),
                                    onHide: () => setTripShown(false),
                                } : null}
                            />

                            {renderPriceNotes('main')}

                            {(score != null || statItems.length > 0) && (
                                <section className="lfv-section lfv-area">
                                    <div className="lfv-area-head">
                                        <h3>{place ? `${swedish ? 'Livet i' : 'Life in'} ${place}` : (swedish ? 'Området' : 'The area')}</h3>
                                        {score != null ? (
                                            <span className="lfv-area-chip">
                                                <b style={{ background: scoreColor, color: readableTextOn(scoreColor) }}>{formatScoreText(score, swedish)}</b>
                                                {areaLabel ? `${areaLabel} ${swedish ? 'område' : 'area'}` : (swedish ? 'Områdesbetyg' : 'Area score')}
                                            </span>
                                        ) : null}
                                        {score != null && (
                                            <div className="lfv-scale" aria-label={`${swedish ? 'Områdesbetyg' : 'Area score'} ${formatScoreText(score, swedish)} ${swedish ? 'av' : 'of'} 6`}>
                                                <span className="lfv-scale-mark" style={{ left: `${scorePosition}%` }} />
                                                <div className="lfv-scale-bar" aria-hidden="true">
                                                    {SCALE_COLORS.map((color) => <i key={color} style={{ background: color }} />)}
                                                </div>
                                                <div className="lfv-scale-ends" aria-hidden="true">
                                                    <span>{scaleLabels[0]}</span>
                                                    <span>{scaleLabels[scaleLabels.length - 1]}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {statItems.length > 0 && (
                                        <div className="lfv-stats">
                                            {statItems.map((item) => (
                                                <div key={item.id}>
                                                    <b>{item.value}</b>
                                                    <span>
                                                        {item.score != null && (
                                                            <i className="lfv-dot" style={{ background: scoreToColor[item.score] }} aria-hidden="true" />
                                                        )}
                                                        {item.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {hasAreaTrend && (
                                        <AreaPriceChart
                                            series={priceTrendArea.series}
                                            estimate={priceHistory ? priceHistory.estimate : null}
                                            homeSqm={homeSqmForChart}
                                            ownSales={priceHistory ? priceHistory.ownSales : null}
                                            swedish={swedish}
                                            formatInt={formatSwedishInteger}
                                        />
                                    )}
                                </section>
                            )}

                            {listPrice != null && renderCostSection('main')}
                        </div>

                        <div className="lfv-side">
                        <aside className="lfv-card">
                            <div className="lfv-price">{priceText}</div>
                            {renderPriceNotes('card')}
                            {renderMonthlyCost()}
                            {listPrice != null && renderCardCostFields('card')}
                            {realtorButton('lfv-realtor--card')}
                            {brokerValue && !isMissingValue(brokerValue) ? (
                                <p className="lfv-broker">{swedish ? 'Mäklare' : 'Realtor'}: {brokerValue}</p>
                            ) : null}
                        </aside>
                        </div>
                    </div>
                </div>

                <div className="lfv-bar">
                    <div>
                        <b>{priceText}</b>
                        {monthlyText ? <small>{swedish ? 'ca ' : 'about '}{monthlyText}</small> : null}
                    </div>
                    {realtorButton('lfv-realtor--bar')}
                </div>

                {galleryOpen && (
                    <div
                        className="lfv-gallery"
                        role="dialog"
                        aria-modal="true"
                        aria-label={swedish ? 'Bilder' : 'Photos'}
                    >
                        <div className="lfv-gallery-top">
                            <button type="button" className="lfv-gallery-close" onClick={() => setGalleryOpen(false)}>
                                <LfvIcon name="back" />
                                {swedish ? 'Tillbaka' : 'Back'}
                            </button>
                            <div className="lfv-gallery-title">
                                <b>{title}</b>
                                <span>{[subtitleLocation || subtitle, priceText].filter(Boolean).join(' · ')}</span>
                            </div>
                            <span className="lfv-gallery-count">
                                {galleryItems[imageIndex]?.isPlanritning
                                    ? (swedish ? 'Planritning' : 'Floor plan')
                                    : `${imageIndex + 1} ${swedish ? 'av' : 'of'} ${images.length}`}
                            </span>
                            <FavoriteButton listing={listing} swedish={swedish} source="gallery" className="lfv-gallery-favorite" />
                            {hasRealtorLink ? realtorButton('lfv-realtor--gallery') : null}
                        </div>
                        <div className="lfv-gallery-body">
                            {images.length > 1 && (
                                <div className="lfv-gallery-grid">
                                    {galleryItems.map((item, index) => (
                                        <button
                                            key={`grid-${item.url}`}
                                            type="button"
                                            className={`lfv-strip-thumb${index === imageIndex ? ' is-active' : ''}${item.isPlanritning ? ' is-plan' : ''}`}
                                            onClick={() => jumpToImage(index)}
                                            aria-label={item.isPlanritning
                                                ? (swedish ? 'Visa planritning' : 'Show floor plan')
                                                : (swedish ? `Visa bild ${index + 1}` : `Show image ${index + 1}`)}
                                            ref={index === imageIndex ? activeThumbRef : null}
                                        >
                                            <img src={item.url} alt="" loading="lazy" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="lfv-gallery-feed" ref={galleryFeedRef} onScroll={handleGalleryScroll}>
                                {galleryItems.map((item, index) => (
                                    <figure
                                        key={`feed-${item.url}`}
                                        className={`lfv-gallery-item${item.isPlanritning ? ' is-plan' : ''}`}
                                        ref={(node) => { galleryItemRefs.current[index] = node; }}
                                    >
                                        <img
                                            src={item.url}
                                            alt={item.isPlanritning ? (swedish ? 'Planritning' : 'Floor plan') : `${title} ${index + 1}`}
                                            loading={Math.abs(index - imageIndex) <= 2 ? 'eager' : 'lazy'}
                                        />
                                    </figure>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>,
        document.body,
    );
};

export default ListingFullViewModal;
